import { ComponentBase } from "../core/ComponentBase.js";
import { ThreadStateStore } from "./ai-workspace/ThreadStateStore.js";

export class AdvancedAIWorkspace extends ComponentBase {
    static styles = `
        .ai-workspace-wrapper {
            display: flex;
            height: 100%;
            background-color: var(--bs-body-bg);
            color: var(--bs-body-color);
            overflow: hidden;
            position: relative;
        }
        .ai-workspace-sidebar-container {
            width: 340px;
            flex-shrink: 0;
            border-right: 1px solid var(--bs-border-color-translucent);
            background-color: var(--bs-tertiary-bg);
            transition: width var(--ab-t-slow), transform var(--ab-t-slow);
            z-index: var(--ab-z-sidebar);
            overflow: hidden;
        }
        .ai-workspace-sidebar-container.desktop-collapsed {
            width: 0;
            border-right: none;
        }
        .ai-workspace-main-container {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            min-width: 0;
            position: relative;
            overflow: hidden;
        }
        .ai-workspace-chat-container {
            flex-grow: 1;
            position: relative;
            overflow: hidden;
        }
        .ai-workspace-chat-container::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 20px;
            height: 120px;
            background: linear-gradient(to bottom, transparent 0%, var(--bs-body-bg) 100%);
            pointer-events: none;
            z-index: 5;
        }
        .ai-workspace-input-overlay {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: var(--ab-z-raised);
            pointer-events: none;
            transition: right var(--ab-t-slow);
        }
        .ai-workspace-input-overlay > * {
            pointer-events: auto;
        }
        @media (max-width: 768px) {
            .ai-workspace-chat-container::after {
                right: 0;
                height: 80px;
            }
        }
        .ai-sidebar-overlay {
            display: none;
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5);
            z-index: var(--ab-z-header);
            backdrop-filter: blur(2px);
        }
        @media (max-width: 768px) {
            .ai-workspace-sidebar-container {
                position: absolute; top: 0; bottom: 0; left: 0;
                transform: translateX(-100%);
            }
            .ai-workspace-sidebar-container.open { transform: translateX(0); }
            .ai-workspace-wrapper.sidebar-open .ai-sidebar-overlay { display: block; }
        }

        /* singleAgentMode rules */
        .ai-workspace-wrapper.single-agent-mode .ai-workspace-sidebar-container {
            display: none !important;
        }
        .ai-workspace-wrapper.single-agent-mode .ai-sidebar-overlay {
            display: none !important;
        }
        .ai-workspace-wrapper.single-agent-mode #ws-sidebar-toggle {
            display: none !important;
        }
        .ai-workspace-wrapper.single-agent-mode #view-info-btn {
            display: none !important;
        }
        .ai-workspace-wrapper.single-agent-mode .ws-chat-header {
            background-color: var(--bs-body-bg) !important;
        }

        /* height modes */
        .ai-workspace-wrapper.ai-workspace-height-viewport {
            height: 100%;
        }
        .ai-workspace-wrapper.ai-workspace-height-container {
            height: 100%;
        }
        .ai-workspace-wrapper.ai-workspace-height-auto {
            height: auto;
            min-height: 400px;
            max-height: 80vh;
        }
    `;

    constructor(config) {
        super(config);

        this.currentProjectId = null;
        this.currentThreadId = null;
        this.currentAgentName = null;
        this.agents = [];
        this.models = [];

        this.singleAgentMode = !!(config.singleAgentMode || config.options?.singleAgentMode);
        this.presetAgentName = config.agentName || config.options?.agentName || null;
        this.disableTelemetry = config.disableTelemetry ?? config.options?.disableTelemetry ?? true;
        if (this.singleAgentMode && this.presetAgentName) {
            this.currentAgentName = this.presetAgentName;
        }

        this.sidebarComponent = null;
        this.chatAreaComponent = null;
        this.inputComponent = null;

        // Phase 1: State Store
        this.store = new ThreadStateStore({ maxSnapshots: this.config.options?.domSnapshotLimit ?? 5 });
        this._agentCapCache = new Map();

        this._boundHandleProjectSelected = this.handleProjectSelected.bind(this);
        this._boundHashChange = this.handleHashChange.bind(this);
        this._boundBeforeUnload = this._handleBeforeUnload.bind(this);
        this._boundHandleAgentResponse = this._handleAgentResponseGlobal.bind(this);
        this._boundHandleChatEvent = this._handleChatEventGlobal.bind(this);
        this._boundHandleAgentAvatarUpdated = this.handleAgentAvatarUpdated.bind(this);
        this._boundWindowFocus = () => {
            if (this.currentThreadId && !document.hidden && document.hasFocus()) {
                const state = this.store.getOrCreate(this.currentThreadId);
                if (state.unreadCount > 0) {
                    state.unreadCount = 0;
                    this.sidebarComponent?.updateThreadBadge(this.currentThreadId, state);
                }
                this.chatAreaComponent?.markAsRead(this.currentThreadId).catch(() => { });
            }
        };

        this.applyStyles();
    }

    async initialize() {
        this.appClient.events.local.on('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.server.on('agentResponse', this._boundHandleAgentResponse);
        this.appClient.events.server.on('chatEvent', this._boundHandleChatEvent);
        this.appClient.events.local.on('AGENT_AVATAR_UPDATED', this._boundHandleAgentAvatarUpdated);
        if (!this.singleAgentMode) {
            this.appClient.events.local.on('SHOW_CONTACT_PICKER', () => this.chatAreaComponent?.showContactPicker('new_chat'));
            window.addEventListener('hashchange', this._boundHashChange);
        }
        window.addEventListener('beforeunload', this._boundBeforeUnload);
        window.addEventListener('focus', this._boundWindowFocus);
        document.addEventListener('visibilitychange', this._boundWindowFocus);

        if (!this.singleAgentMode) {
            // Register framework-level configurable workspace keybindings
            this.appClient.hotkeys.register('newChat',
                { key: 'n', ctrlKey: true, altKey: true },
                'Start a new conversation',
                () => this.appClient.events.local.emit('SHOW_CONTACT_PICKER')
            );
            this.appClient.hotkeys.register('globalSearch',
                { key: 'f', ctrlKey: true, shiftKey: true },
                'Toggle search messages overlay',
                () => this.appClient.events.local.emit('TOGGLE_CHAT_SEARCH')
            );
            this.appClient.hotkeys.register('filePanel',
                { key: 'u', ctrlKey: true, shiftKey: true },
                'Toggle agent files drawer',
                () => this.appClient.events.local.emit('TOGGLE_AGENT_FILES')
            );
            this.appClient.hotkeys.register('vnc',
                { key: 'v', ctrlKey: true, altKey: true },
                'Toggle VNC/Sandbox',
                () => this.appClient.events.local.emit('TOGGLE_VNC')
            );
        }

        await super.initialize();
    }

    async initAfterLogin() {
        try {
            const [agentsRes, modelsRes] = await Promise.all([
                this.appClient.rpcClient.call('AgentConfigRpc.getData', { pageSize: 100 }),
                this.appClient.rpcClient.call('AIConfigRpc.getModels', {}).catch(() => ([]))
            ]);
            this.agents = agentsRes.data || [];
            this.models = modelsRes || [];
            if (this.singleAgentMode && this.presetAgentName) {
                this.currentAgentName = this.presetAgentName;
            } else if (this.agents.length > 0) {
                this.currentAgentName = this.agents[0].name;
            }
        } catch (e) {
            this.log.warn("Could not load agents or models for workspace.");
        }

        const selectedProject = this.appClient.getSharedData('selectedProject');
        if (selectedProject) {
            this.currentProjectId = selectedProject._id;
        }

        if (this.singleAgentMode && this.sidebarComponent) {
            try {
                await this.sidebarComponent.loadThreads(this.currentProjectId);
                await this.loadSingleAgentThread();
            } catch (err) {
                this.log.error("Failed to load threads in initAfterLogin:", err);
            }
        }
    }

    isSandboxSupported() {
        const agentName = this.currentAgentName;
        if (!agentName) return false;
        const agent = this.agents.find(a => a.name === agentName);
        return !!(agent?.capabilities?.sandbox);
    }

    isVoiceSupported() {
        const agentName = this.currentAgentName;
        if (!agentName) return false;
        const agent = this.agents.find(a => a.name === agentName);
        return !!(agent?.capabilities?.voiceEnabled);
    }

    isFileUploadSupported() {
        const agentName = this.currentAgentName;
        if (!agentName) return true; // Default to true for human-only threads
        const agent = this.agents.find(a => a.name === agentName);
        if (!agent) return true;
        const modelId = agent.model;
        if (!modelId) return true;

        const modelSpec = this.models.find(m => m.id === modelId || m.name === modelId);
        if (!modelSpec) {
            // Fallback for known multimodal model families
            const lowerId = modelId.toLowerCase();
            if (lowerId.includes('gpt-4o') || lowerId.includes('gpt-5') || lowerId.includes('gemini') || lowerId.includes('claude-3') || lowerId.includes('claude-3-5')) {
                return true;
            }
            return true;
        }

        return !!(modelSpec.capabilities?.multimodal || modelSpec.modality === 'multimodal' || (modelSpec.modalities && modelSpec.modalities.includes('image-in')));
    }

    async handleProjectSelected({ projectData }) {
        const newProjectId = projectData ? projectData._id : null;
        if (this.currentProjectId === newProjectId) return;

        this.currentProjectId = newProjectId;
        this.currentThreadId = null;
        this.updateHash(null);
        // Wipe all stale per-thread DOM snapshots and state from the previous project
        this.store.clear();
        if (this.chatAreaComponent) this.chatAreaComponent.clearChat();

        if (this.sidebarComponent) {
            await this.sidebarComponent.loadThreads(this.currentProjectId);
            if (this.singleAgentMode) {
                await this.loadSingleAgentThread();
            } else {
                await this.handleHashChange();
            }
        }
    }

    async setContext(context) {
        if (!context) return;
        const newProjectId = context.projectId || null;
        if (this.currentProjectId === newProjectId) return;

        this.currentProjectId = newProjectId;
        this.currentThreadId = null;
        this.updateHash(null);
        // Wipe all stale per-thread DOM snapshots and state from the previous project
        this.store.clear();
        if (this.chatAreaComponent) this.chatAreaComponent.clearChat();

        if (this.sidebarComponent) {
            await this.sidebarComponent.loadThreads(this.currentProjectId);
            if (this.singleAgentMode) {
                await this.loadSingleAgentThread();
            } else {
                await this.handleHashChange();
            }
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'ai-workspace-wrapper';
            if (this.singleAgentMode) {
                this.container.classList.add('single-agent-mode');
                const heightMode = this.config.height || this.config.options?.height || 'viewport';
                this.container.classList.add(`ai-workspace-height-${heightMode}`);
            }

            const sidebarContainer = document.createElement('div');
            sidebarContainer.className = 'ai-workspace-sidebar-container';
            if (this.singleAgentMode) {
                sidebarContainer.style.display = 'none';
            }
            this.container.appendChild(sidebarContainer);

            const overlay = document.createElement('div');
            overlay.className = 'ai-sidebar-overlay';
            if (this.singleAgentMode) {
                overlay.style.display = 'none';
            }
            overlay.addEventListener('click', () => this.toggleSidebar(false));
            this.container.appendChild(overlay);

            const mainContainer = document.createElement('div');
            mainContainer.className = 'ai-workspace-main-container';

            const chatWrap = document.createElement('div');
            chatWrap.className = 'ai-workspace-chat-container';

            const chatAreaContainer = document.createElement('div');
            chatAreaContainer.style.height = '100%';
            chatAreaContainer.style.overflow = 'hidden';

            const inputOverlay = document.createElement('div');
            inputOverlay.className = 'ai-workspace-input-overlay';
            const inputContainer = document.createElement('div');
            inputOverlay.appendChild(inputContainer);

            chatWrap.appendChild(chatAreaContainer);
            chatWrap.appendChild(inputOverlay);
            mainContainer.appendChild(chatWrap);
            this.container.appendChild(mainContainer);

            this.sidebarComponent = await this.createManagedComponent('ai-workspace/WorkspaceSidebar', sidebarContainer, {
                instanceId: `${this.config.instanceId}-sidebar`,
                workspace: this
            });

            this.chatAreaComponent = await this.createManagedComponent('ai-workspace/WorkspaceChatArea', chatAreaContainer, {
                instanceId: `${this.config.instanceId}-chatarea`,
                workspace: this
            });

            this.inputComponent = await this.createManagedComponent('ai-workspace/WorkspaceChatInput', inputContainer, {
                instanceId: `${this.config.instanceId}-input`,
                workspace: this
            });

            this._inputResizeObserver = new ResizeObserver(() => {
                const inputH = inputContainer.offsetHeight;
                const msgContainer = this.chatAreaComponent?.messagesContainer;
                if (msgContainer) msgContainer.style.paddingBottom = (inputH + 24) + 'px';
                const bubble = this.chatAreaComponent?.thinkingBubble;
                if (bubble) bubble.style.bottom = (inputH + 12) + 'px';
                const scrollBtn = this.chatAreaComponent?.scrollBtn;
                if (scrollBtn) scrollBtn.style.bottom = (inputH + 12) + 'px';
            });
            this._inputResizeObserver.observe(inputContainer);

            if (this.appClient.isLoggedIn() && (this.currentProjectId || this.singleAgentMode)) {
                try {
                    await this.sidebarComponent.loadThreads(this.currentProjectId);
                    if (this.singleAgentMode) {
                        await this.loadSingleAgentThread();
                    } else {
                        await this.handleHashChange();
                    }
                } catch (err) {
                    this.log.error("Failed to load threads in render:", err);
                }
            }
        }
        return this.container;
    }

    async handleHashChange() {
        if (this.singleAgentMode) return;
        if (!this.currentProjectId) return;

        const hash = window.location.hash;
        if (hash.startsWith('#thread=')) {
            const threadId = hash.split('=')[1];
            if (this.currentThreadId !== threadId) {
                await this.loadThread(threadId);
            }
        } else {
            await this.startNewThread();
        }
    }

    async loadSingleAgentThread() {
        if (!this.appClient.isLoggedIn()) return;
        if (!this.currentAgentName) return;
        if (this.currentThreadId) return;
        const existingThread = this.sidebarComponent?.allThreads?.find(t =>
            t.agentName === this.currentAgentName &&
            (t.type === 'ai_only' || !t.type || t.type === 'standard')
        );
        if (existingThread) {
            await this.loadThread(existingThread._id);
        } else {
            await this.ensureThread();
        }
    }

    updateHash(threadId) {
        if (this.singleAgentMode) return;
        if (threadId) {
            window.history.pushState(null, null, `#thread=${threadId}`);
        } else {
            const noHashURL = window.location.href.replace(/#.*$/, '');
            window.history.pushState(null, null, noHashURL);
        }
    }

    async loadThread(threadId, agentNameOverride = null) {
        this._flushCurrentThread();

        // 1. Save current thread snapshot
        if (this.currentThreadId && this.chatAreaComponent) {
            const snap = this.chatAreaComponent.detachMessages?.();
            if (snap) {
                this.store.saveSnapshot(this.currentThreadId, snap.dom, snap.scrollTop, snap.chatListState);
            }
        }

        this.currentThreadId = threadId;

        if (!this.singleAgentMode) {
            const thread = this.sidebarComponent?.allThreads.find(t => t._id === threadId);
            if (thread) {
                // Prefer top-level agentName (backward compat). For group threads where it may
                // be null, fall back to the first agent member so sendMessage always has a target.
                const agentMember = (thread.members || []).find(m => m.participantType === 'agent');
                this.currentAgentName = thread.agentName || agentMember?.participantId || this.currentAgentName;
            } else if (agentNameOverride) {
                this.currentAgentName = agentNameOverride;
            }
        }

        this.updateHash(threadId);
        this.sidebarComponent?.updateSelection(threadId);

        // 2. Fetch agent capabilities if not cached (Future)

        // 3. Restore or Load
        const state = this.store.getOrCreate(threadId);
        let loadedFromSnapshot = false;
        if (state.domSnapshot && this.chatAreaComponent) {
            this.chatAreaComponent.attachMessages?.(state.domSnapshot, state.scrollTop, state.chatListState);
            this.chatAreaComponent.applyLiveState?.(state);
            loadedFromSnapshot = true;
        } else if (this.chatAreaComponent) {
            await this.chatAreaComponent.loadHistory(threadId);
            this.chatAreaComponent.applyLiveState?.(state);
        }
        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps || []);

        // 4. Clear unread indicators
        state.hasNewActivity = false;
        state.unreadCount = 0;
        this.sidebarComponent?.updateThreadBadge(threadId, state);
        if (this.chatAreaComponent?.markAsRead) {
            this.chatAreaComponent.markAsRead(threadId);
        }

        // Execute pending jump instantly if we restored from a snapshot (all DOM nodes already present)
        if (loadedFromSnapshot && this._pendingMessageJump && this.chatAreaComponent) {
            const pendingJump = this._pendingMessageJump;
            if (this.chatAreaComponent.chatList) {
                this.chatAreaComponent.chatList._suppressScrollPin = true;
            }
            this.chatAreaComponent.jumpToMessage(pendingJump);
        }

        if (window.innerWidth <= 768) this.toggleSidebar(false);
    }

    async startNewThread(agentName = null) {
        this._flushCurrentThread();

        if (this.currentThreadId && this.chatAreaComponent) {
            const snap = this.chatAreaComponent.detachMessages?.();
            if (snap) {
                this.store.saveSnapshot(this.currentThreadId, snap.dom, snap.scrollTop, snap.chatListState);
            }
        }

        this.currentThreadId = null;
        this.currentAgentName = agentName || this.currentAgentName || (this.agents[0] ? this.agents[0].name : 'PrimaryAssistant');
        this.updateHash(null);
        this.sidebarComponent?.clearSelection();

        if (this.chatAreaComponent) {
            this.chatAreaComponent.clearChat();
        }

        if (window.innerWidth <= 768) this.toggleSidebar(false);
    }


    async ensureThread() {
        if (!this.appClient.isLoggedIn()) return false;
        if (this.currentThreadId) return true;
        try {
            const newThread = await this.appClient.rpcClient.call('ThreadRpc.create', {
                title: "New Conversation",
                agentName: this.currentAgentName,
                projectId: this.currentProjectId
            }, { projectId: this.currentProjectId });

            this.sidebarComponent?.addThreadOptimistically(newThread);
            await this.loadThread(newThread._id);

            // Wait a few seconds then generate a better title
            setTimeout(async () => {
                const res = await this.appClient.rpcClient.call('ThreadRpc.generateTitle', { threadId: this.currentThreadId }, { projectId: this.currentProjectId });
                if (res.success && this.sidebarComponent) {
                    this.sidebarComponent.updateThreadTitle(this.currentThreadId, res.title);
                }
            }, 5000);

            return true;
        } catch (error) {
            this.log.error("Failed to ensure thread:", error);
            this.appClient.notifier.error(this.t("Failed to create conversation."));
            return false;
        }
    }

    async sendMessage(messageText, attachments = [], options = {}) {
        if (!messageText && attachments.length === 0 && !options.submitOnlyPending) return;

        if (!(await this.ensureThread())) return;

        const state = this.store.getOrCreate(this.currentThreadId);
        const queued = state.pendingFollowUps.filter(item => item.status === 'pending' || item.status === 'failed');
        const messagesToSubmit = [
            ...queued.map(item => ({ content: item.content, attachments: item.attachments || [] })),
            ...(options.submitOnlyPending ? [] : [{ content: messageText, attachments }])
        ].filter(item => item.content || item.attachments.length > 0);
        if (messagesToSubmit.length === 0) return;
        const outboundMessage = this._formatBatchMessage(messagesToSubmit);
        const outboundAttachments = messagesToSubmit.flatMap(item => item.attachments || []);

        for (const item of queued) item.status = 'submitting';
        state.isProcessing = true;
        if (this.inputComponent) this.inputComponent.setProcessingState(true);
        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);

        if (this.chatAreaComponent) {
            for (const item of messagesToSubmit) {
                this.chatAreaComponent.addMessageToUI({
                    role: 'user',
                    content: item.content,
                    attachments: item.attachments
                }, true);
            }
            this.chatAreaComponent.scrollToBottom?.();
            this.chatAreaComponent.applyLiveState(state);
        }

        try {
            // Only call the AI if there is an agent in this thread
            if (this.currentAgentName) {
                await this.appClient.rpcClient.call('AiRpc.sendMessage', {
                    agentName: this.currentAgentName,
                    message: outboundMessage,
                    attachments: outboundAttachments,
                    threadId: this.currentThreadId
                }, { projectId: this.currentProjectId });
                state.pendingFollowUps = state.pendingFollowUps.filter(item => !queued.some(q => q.id === item.id));
                this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
            } else {
                // Pure human thread — no AI call, clear processing state immediately
                state.isProcessing = false;
                if (this.inputComponent) this.inputComponent.setProcessingState(false);
            }
        } catch (error) {
            state.isProcessing = false;
            for (const item of queued) {
                item.status = 'failed';
                item.error = error.message;
            }
            if (this.inputComponent) this.inputComponent.setProcessingState(false);
            this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
            if (this.chatAreaComponent) {
                this.chatAreaComponent.addMessageToUI({ role: 'system', content: `Error: ${error.message}` });
                this.chatAreaComponent.applyLiveState(state);
            }
        }
    }

    _formatBatchMessage(items = []) {
        const textItems = items.map((item, index) => {
            const content = item.content || '';
            const attachmentNote = item.attachments?.length ? `\n[${item.attachments.length} attachment(s)]` : '';
            return `Message ${index + 1}:\n${content}${attachmentNote}`;
        });
        return textItems.length === 1 ? (items[0].content || '') : `The user submitted these messages in order. Treat them as one ordered update:\n\n${textItems.join('\n\n')}`;
    }

    queueFollowUpMessage(messageText, attachments = []) {
        if (!this.currentThreadId) return false;
        const state = this.store.getOrCreate(this.currentThreadId);
        state.pendingFollowUps.push({
            id: `followup-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            content: messageText,
            attachments,
            status: 'pending',
        });
        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
        return true;
    }

    revokeFollowUpMessage(followUpId) {
        if (!this.currentThreadId) return;
        const state = this.store.getOrCreate(this.currentThreadId);
        state.pendingFollowUps = state.pendingFollowUps.filter(item => item.id !== followUpId || item.status === 'submitting');
        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
    }

    clearPendingFollowUps(threadId = this.currentThreadId) {
        if (!threadId) return;
        const state = this.store.getOrCreate(threadId);
        state.pendingFollowUps = state.pendingFollowUps.filter(item => item.status === 'submitting');
        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
    }

    async submitPendingFollowUps(threadId = this.currentThreadId) {
        if (!threadId || threadId !== this.currentThreadId) return;
        const state = this.store.getOrCreate(threadId);
        const pending = state.pendingFollowUps.filter(item => item.status === 'pending' || item.status === 'failed');
        if (pending.length === 0) return;
        await this.sendMessage('', [], { submitOnlyPending: true });
    }

    async flushPendingFollowUps(threadId = this.currentThreadId, options = {}) {
        if (!threadId || threadId !== this.currentThreadId || !this.currentAgentName) return;
        const state = this.store.getOrCreate(threadId);
        const eligibleStatuses = options.manual ? ['pending', 'failed'] : ['pending'];
        const pending = state.pendingFollowUps.filter(item => eligibleStatuses.includes(item.status));
        if (pending.length === 0) return;

        for (const item of pending) item.status = 'submitting';
        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);

        for (const item of pending) {
            try {
                await this.appClient.rpcClient.call('AiRpc.sendMessage', {
                    agentName: this.currentAgentName,
                    message: item.content,
                    attachments: item.attachments,
                    threadId
                }, { projectId: this.currentProjectId });

                if (this.chatAreaComponent) {
                    this.chatAreaComponent.addMessageToUI({
                        role: 'user',
                        content: item.content,
                        attachments: item.attachments
                    });
                }
                state.pendingFollowUps = state.pendingFollowUps.filter(existing => existing.id !== item.id);
            } catch (error) {
                item.status = 'failed';
                item.error = error.message;
            }
        }

        this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
    }

    async cancelGeneration() {
        if (!this.currentAgentName || !this.currentThreadId) return;
        const state = this.store.getOrCreate(this.currentThreadId);
        state.isProcessing = false;
        if (this.inputComponent) this.inputComponent.setProcessingState(false);
        if (this.chatAreaComponent) this.chatAreaComponent.applyLiveState(state);

        try {
            await this.appClient.rpcClient.call('AiRpc.cancelProcessing', {
                agentName: this.currentAgentName,
                threadId: this.currentThreadId,
            }, { projectId: this.currentProjectId });
        } catch (e) {
            this.log.error("Failed to cancel processing", e);
        }
    }

    // ── Global Event Handlers ──────────────────────────────────────────────

    _handleAgentResponseGlobal(payload) {
        const { threadId, type } = payload;
        if (!threadId) return; // Ignore legacy unconnected events

        const state = this.store.getOrCreate(threadId);
        const isActive = (threadId === this.currentThreadId);

        // Update State
        if (type === 'status') {
            state.isProcessing = (payload.status === 'processing');
        } else if (type === 'message_delta') {
            state.streamBuffer += payload.delta || '';
        }

        if (type === 'tool_event') {
            const eventName = payload.event?.eventName || '';
            if (eventName === 'agentic.task.complete' || eventName === 'agentic.task.fail') {
                this.flushPendingFollowUps(threadId).catch(e => this.log.error("Failed to flush follow-up messages", e));
            }
        }

        // Forward to live view
        if (isActive && this.chatAreaComponent?.handleLiveEvent) {
            this.chatAreaComponent.handleLiveEvent(payload, state);
            if (this.inputComponent?.setProcessingState) {
                this.inputComponent.setProcessingState(state.isProcessing);
            }
        }

        if (type === 'message_done' || type === 'error' || type === 'message') {
            const completedContent = state.streamBuffer; // Capture before clearing
            state.streamBuffer = ''; // Clear stream buffer when turn completes AFTER forwarding
            if (!isActive) {
                state.hasNewActivity = true;
                // Increment for every message type that represents a completed AI turn:
                // message_done (streaming), message (direct/scheduled), error
                if (type === 'message_done' || type === 'error' || type === 'message') {
                    state.unreadCount++;
                }
                state.domSnapshot = null; // Invalidate cached DOM snapshot so it pulls fresh history
            } else if ((type === 'message_done' || type === 'error') && !document.hidden && document.hasFocus()) {
                this.chatAreaComponent?.markAsRead(threadId).catch(() => { });
            }

            // Update sidebar thread preview when AI finishes a turn (in-place, no flash)
            if ((type === 'message_done' || type === 'message') && this.sidebarComponent) {
                const previewContent = (type === 'message' ? (payload.message?.content || '') : completedContent);
                if (previewContent) {
                    const preview = previewContent.replace(/[#*`>]/g, '').trim();
                    this.sidebarComponent.updateThreadPreview(threadId, preview, new Date().toISOString());
                }
            }

            if (type === 'message_done' || type === 'error' || type === 'message') {
                const agentName = payload.agentName || payload.message?.senderId || 'AI Assistant';
                const action = `#thread=${threadId}`;
                const title = type === 'error'
                    ? `🤖 ${this.t('{agentName} Failed Task', { agentName })}`
                    : `🤖 ${this.t('{agentName} Completed Task', { agentName })}`;
                const body = type === 'error'
                    ? `${this.t('Error')}: ${payload.error || this.t('Turn failed')}`
                    : (completedContent || payload.message?.content || this.t('Task successfully completed'))
                        .replace(/[#*`>]/g, '').trim().slice(0, 120) || this.t('Task successfully completed');

                this.appClient.notifier.desktopNotification(title, body, {
                    tag: `thread-${threadId}`,
                    action: action
                }).catch(() => { });
            }

            if (state.pendingFollowUps.length > 0 && payload.error !== 'cancelled') {
                this.flushPendingFollowUps(threadId).catch(e => this.log.error("Failed to flush follow-up messages after turn", e));
            } else if (payload.error === 'cancelled') {
                this.inputComponent?.renderPendingFollowUps?.(state.pendingFollowUps);
            }
        }

        // Update Sidebar
        this.sidebarComponent?.updateThreadBadge(threadId, state);
    }

    _handleChatEventGlobal(payload) {
        const { threadId, type, message, thread } = payload;
        if (!threadId) return;

        const isActive = (threadId === this.currentThreadId);

        if (type === 'new_message' && message) {
            const state = this.store.getOrCreate(threadId);
            const isFromOther = message.senderType !== 'user';

            if (!isActive) {
                state.hasNewActivity = true;
                if (isFromOther) {
                    state.unreadCount++;
                }
                state.domSnapshot = null; // Invalidate cached DOM snapshot so it pulls fresh history
                this.sidebarComponent?.updateThreadBadge(threadId, state);
            } else if (isFromOther && this.chatAreaComponent) {
                this.chatAreaComponent.addMessageToUI(message);
                if (!document.hidden && document.hasFocus()) {
                    this.chatAreaComponent.markAsRead(threadId).catch(() => { });
                }
            }

            // Desktop notification for messages from agents/AI when the app is not in focus.
            // Uses the same tag as agentResponse notifications so the browser deduplicates
            // if both events fire for the same message (streaming + stored chatEvent).
            if (isFromOther) {
                const agentName = message.senderId || payload.agentName || 'AI Assistant';
                const preview = (message.content || '').replace(/[#*`>]/g, '').trim();
                this.appClient.notifier.desktopNotification(
                    `🤖 ${this.t('New message from {agentName}', { agentName })}`,
                    preview.slice(0, 120) || this.t('New message'),
                    { tag: `thread-${threadId}`, action: `#thread=${threadId}` }
                ).catch(() => { });
            }

            // Update sidebar thread preview in-place (no flash, no full re-render)
            if (this.sidebarComponent) {
                const preview = (message.content || '').replace(/[#*`>]/g, '').trim();
                this.sidebarComponent.updateThreadPreview(
                    threadId, preview, message.createdAt || new Date().toISOString()
                );
            }
        } else if (type === 'thread_updated' && thread) {
            if (this.sidebarComponent) this.sidebarComponent.updateThreadOptimistically(thread);
        } else if (type === 'member_changed' && thread) {
            // Update the sidebar's cached thread
            if (this.sidebarComponent) {
                this.sidebarComponent.updateThreadOptimistically(thread);
            }
            // If the active thread changed, re-render header + info panel
            if (isActive && this.chatAreaComponent) {
                this.chatAreaComponent._currentThread = thread;
                this.chatAreaComponent.renderHeader();
                if (this.chatAreaComponent.infoPanel?.isOpen) {
                    this.chatAreaComponent.infoPanel.setThread(thread);
                }
            }

        }
    }

    // ────────────────────────────────────────────────────────────────────────

    toggleSidebar(forceState) {
        if (!this.container) return;
        const sidebar = this.container.querySelector('.ai-workspace-sidebar-container');
        const isOpen = forceState !== undefined ? forceState : !sidebar.classList.contains('open');

        if (isOpen) {
            sidebar.classList.add('open');
            this.container.classList.add('sidebar-open');
        } else {
            sidebar.classList.remove('open');
            this.container.classList.remove('sidebar-open');
        }
    }

    toggleDesktopSidebar() {
        if (!this.container) return;
        const sidebar = this.container.querySelector('.ai-workspace-sidebar-container');
        sidebar.classList.toggle('desktop-collapsed');
    }

    _flushCurrentThread() {
        const threadId = this.currentThreadId;
        const agentName = this.currentAgentName;
        const projectId = this.currentProjectId;
        if (!threadId || !agentName) return;

        this.appClient.rpcClient
            .call('AiRpc.flushMemory', { agentName, threadId }, { projectId })
            .catch(() => { });
    }

    _handleBeforeUnload() {
        this._flushCurrentThread();
    }

    destroy() {
        this.appClient.events.local.off('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.server.off('agentResponse', this._boundHandleAgentResponse);
        this.appClient.events.server.off('chatEvent', this._boundHandleChatEvent);
        this.appClient.events.local.off('AGENT_AVATAR_UPDATED', this._boundHandleAgentAvatarUpdated);
        if (!this.singleAgentMode) {
            window.removeEventListener('hashchange', this._boundHashChange);
        }
        window.removeEventListener('beforeunload', this._boundBeforeUnload);
        window.removeEventListener('focus', this._boundWindowFocus);
        document.removeEventListener('visibilitychange', this._boundWindowFocus);

        if (!this.singleAgentMode) {
            // Unregister workspace hotkeys
            this.appClient.hotkeys.unregister('newChat');
            this.appClient.hotkeys.unregister('globalSearch');
            this.appClient.hotkeys.unregister('filePanel');
            this.appClient.hotkeys.unregister('vnc');
        }

        this.sidebarComponent?.destroy();
        this.chatAreaComponent?.destroy();
        this.inputComponent?.destroy();
        super.destroy();
    }

    handleAgentAvatarUpdated({ agentId, avatarUrl, name }) {
        if (this.agents) {
            const matchedAgent = this.agents.find(a => a._id === agentId || a.name === name);
            if (matchedAgent) {
                matchedAgent.avatarUrl = avatarUrl;
            }
        }
    }
}
